import { MicrophoneListener } from 'mind-sdk-web';
import { MediaStreamAudioConsumer } from 'mind-sdk-web/lib/MediaStream';
import { Microphone as MindMicrophone } from 'mind-sdk-web/lib/Microphone';
import { DeviceRegistry, MutableAudioSupplier } from '../../../types';

type MicrophoneProps = {
  deviceRegistry: DeviceRegistry;
  mindMicrophone: MindMicrophone;
  decoratedLabel?: string;
  description?: string;
};

export class Microphone implements MicrophoneListener {
  static noiseSuppressionWorkletUrl = MindMicrophone.noiseSuppressionWorkletUrl;

  private static readonly MAX_RETRY = 3;
  private static readonly RETRY_DELAY_MS = 500;
  private retryCount: number = 0;

  private deviceRegistry: DeviceRegistry;
  private mindMicrophone: MindMicrophone;
  private mutableAudioSupplier: MutableAudioSupplier = new MutableAudioSupplier();

  private listener: MicrophoneListener | null = null;

  private acquired: boolean = false;
  private acquiring: boolean = false;

  private decoratedLabel: string;
  private description: string;

  constructor(props: MicrophoneProps) {
    this.deviceRegistry = props.deviceRegistry;
    this.mindMicrophone = props.mindMicrophone;
    this.decoratedLabel = props.decoratedLabel ?? '';
    this.description = props.description ?? '';

    this.mindMicrophone.setListener(this);

    this.mindMicrophone.addAudioConsumer(this.mutableAudioSupplier);
  }

  isDefault(): boolean {
    const defaultMicrophone = this.deviceRegistry
      .getDevicesInfo()
      .find((device) => device.kind === 'audioinput' && device.deviceId === 'default');
    return defaultMicrophone?.deviceId === this.getId() || this.getId() === 'default';
  }

  getDescription(): string {
    return this.description;
  }

  isAcquired(): boolean {
    return this.acquired;
  }

  isAcquiring(): boolean {
    return this.acquiring;
  }

  setAudioConsumerIgnoringMute(consumer: MediaStreamAudioConsumer | null): void {
    console.log(`Setting audio consumer ignoring mute for '${this.mindMicrophone.getLabel()}'`);

    this.mutableAudioSupplier.setRawConsumer(consumer);
  }

  getId(): string {
    return this.mindMicrophone.getId();
  }

  getLabel(): string {
    return this.decoratedLabel ?? this.mindMicrophone.getLabel();
  }

  async acquire(): Promise<void> {
    console.log(`Acquiring microphone '${this.mindMicrophone.getLabel()}'`);

    if (this.acquiring) {
      throw new Error('Microphone is already acquiring');
    }

    this.acquiring = true;
    try {
      await this.mindMicrophone.acquire();
      this.acquired = true;
      this.retryCount = 0;
    } finally {
      this.acquiring = false;
      console.log(`Microphone '${this.mindMicrophone.getLabel()}' acquired: ${this.acquired}`);
    }
  }

  release(): void {
    console.log(`Releasing microphone ${this.mindMicrophone.getLabel()}`);

    if (this.acquiring) console.warn(`Microphone '${this.mindMicrophone.getLabel()}' is acquiring during release`);

    if (!this.acquired) return;

    this.mindMicrophone.release();
    this.acquired = false;
  }

  setNoiseSuppression(noiseSuppression: boolean): void {
    console.log(`Setting noise suppression for microphone '${this.mindMicrophone.getLabel()}': ${noiseSuppression}`);
    this.mindMicrophone.setNoiseSuppression(noiseSuppression);
  }

  setMuted(muted: boolean): void {
    console.log(`Setting microphone '${this.mindMicrophone.getLabel()}' muted: ${muted}`);
    this.mutableAudioSupplier.setMuted(muted);
  }

  addAudioConsumer(consumer: MediaStreamAudioConsumer): void {
    this.mutableAudioSupplier.addAudioConsumer(consumer);
  }

  removeAudioConsumer(consumer: MediaStreamAudioConsumer): void {
    this.mutableAudioSupplier.removeAudioConsumer(consumer);
  }

  destroy(): void {
    console.log(`Destroying microphone ${this.mindMicrophone.getLabel()}`);
    if (this.acquiring) console.warn(`Microphone '${this.mindMicrophone.getLabel()}' is acquiring during destroy`);

    this.mindMicrophone.destroy();
  }

  fireAudioBuffer(): void {
    console.log(`Firing audio buffer for microphone '${this.mindMicrophone.getLabel()}'`);
    this.mindMicrophone.fireAudioBuffer();
  }

  setListener(microphoneListener: MicrophoneListener | null): void {
    this.listener = microphoneListener;
  }

  onMicrophoneReleasedForcibly(): void {
    if (this.acquiring) {
      console.warn('Microphone released forcibly while acquiring');
      if (this.retryCount < Microphone.MAX_RETRY) {
        console.warn('Retrying to acquire microphone...');
        this.retryCount++;
        setTimeout(() => {
          this.acquire().catch((e) => {
            console.warn('Retry acquire failed:', e);
          });
        }, Microphone.RETRY_DELAY_MS);
      } else {
        console.warn('Max microphone acquire retries reached.');
        this.retryCount = 0;
      }
    }

    this.acquired = false;

    if (this.listener) {
      this.listener.onMicrophoneReleasedForcibly(this);
    }
  }
}
